import { z } from 'zod';

// Schemas and types for the ai tool, shared by server and UI.

export const AI_PROVIDERS = ['anthropic', 'openai'] as const;
export type AiProvider = (typeof AI_PROVIDERS)[number];

/** Suggestions only: the model field accepts any id the provider knows. */
export const AI_MODEL_SUGGESTIONS: Record<AiProvider, readonly string[]> = {
  anthropic: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5'],
  openai: ['gpt-5', 'gpt-5-mini', 'llama3.1', 'qwen2.5'],
};

/** Document families the assistant can read as context. */
export const AI_CONTEXT_TYPES = ['word', 'sheet', 'pdf'] as const;
export type AiContextType = (typeof AI_CONTEXT_TYPES)[number];

export const AI_MAX_CONTEXT_FILES = 8;

export const aiSettingsInput = z.object({
  provider: z.enum(AI_PROVIDERS),
  model: z.string().trim().min(1).max(200),
  /** OpenAI-compatible endpoints only; null means the provider's default. */
  baseUrl: z.url().trim().max(500).nullable().optional(),
  /** Optional on update: omitted keeps the stored key (only while provider and base URL stay the same). */
  apiKey: z.string().trim().min(1).max(500).optional(),
});
export type AiSettingsInput = z.infer<typeof aiSettingsInput>;

/** What the server returns about the stored settings: never the key itself. */
export interface AiSettings {
  provider: AiProvider;
  model: string;
  baseUrl: string | null;
  keyHint: string;
}

export const aiChatMessage = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(50_000),
});
export type AiChatMessage = z.infer<typeof aiChatMessage>;

export const aiChatInput = z.object({
  messages: z
    .array(aiChatMessage)
    .min(1)
    .max(80)
    .refine((messages) => messages.at(-1)?.role === 'user', 'The last message must come from the user.'),
  context: z.object({
    tasks: z.boolean(),
    fileIds: z.array(z.uuid()).max(AI_MAX_CONTEXT_FILES),
  }),
});
export type AiChatInput = z.infer<typeof aiChatInput>;

export type AiSourceStatus = 'included' | 'truncated' | 'skipped';

/** How each requested source ended up in the model's context. */
export interface AiContextSource {
  kind: 'tasks' | 'file';
  name: string;
  status: AiSourceStatus;
  /** Why a source was skipped or cut, in plain Turkish. */
  note?: string;
}

/** Server-Sent Events emitted by POST /api/ai/chat, one JSON payload per event. */
export type AiStreamEvent =
  | { event: 'context'; data: { sources: AiContextSource[] } }
  | { event: 'delta'; data: { text: string } }
  | { event: 'done'; data: { stopReason: 'end' | 'length' } }
  | { event: 'error'; data: { message: string } };
